import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import Button from '@mui/material/Button';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { clientHubRefer } from '@/components/client-hub/api';
import style from '@/components/referral/style.module.scss';
import {
  EMAIL_FORM_ERR_FR,
  ENTER_CHARS_FORM_ERR,
  ENTER_VALID_FORM_ERR,
  FORM_ERRORS,
  NAME_FORM_ERR,
  NAME_FORM_ERR_FR,
} from '@/utils/constants';

type Local = 'en' | 'fr';

type Inputs = {
  clientFullName: string;
  clientEmail: string;
  clientPhone: string;
  friendRelationship: string;
  friendName: string;
  friendEmail: string;
  friendPhone: string;
};

interface ClientHubReferralProps {
  isMobile: boolean;
  activeSection: string;
  track: any;
  lcode: string;
  local: Local;
}

const translations: Record<Local, any> = {
  en: {
    referralTitle: 'Referrals',
    description:
      'Refer a friend to Depotium and earn up to $100 when they move in. Just fill in the form below.',
    clientSectionTitle: 'Your contact information',
    clientNameLabel: 'Full name*',
    clientPhoneLabel: 'Phone number on file*',
    clientEmailLabel: 'Email',
    friendSectionTitle: 'The referral information',
    friendRelationshipLabel: 'Relationship to you (friend, coworker, etc)*',
    friendRelationshipDefaultSelect: 'Select relationship',
    friendNameLabel: 'Name*',
    friendPhoneLabel: 'Phone number*',
    friendEmailLabel: 'Email',
    termsText:
      'By submitting a referral I confirm I agree with the referral program terms in the FAQs section.',
    referButton: 'Refer',
    referButtonIWasReferred: 'Submit',
    successHeader: 'Thank you for your referral!',
    successBody: "We'll reach out to you once {recipientName} has moved in.",
    closeButton: 'Close',
    errors: {
      relationshipRequired: 'Relationship required',
      nameRequired: NAME_FORM_ERR,
      namePattern: ENTER_CHARS_FORM_ERR,
      phoneRequired: FORM_ERRORS.EN.ENTER_PHONE,
      phonePattern: FORM_ERRORS.EN.ENTER_PHONE,
      emailPattern: ENTER_VALID_FORM_ERR,
    },
  },
  fr: {
    referralTitle: 'Recommandation',
    description: (
      <>
        Recommandez nos services à un ami et <b>obtenez jusqu’à 100 $</b> lors
        de son emménagement. Il vous suffit de remplir le formulaire ci-dessous.
      </>
    ),
    clientSectionTitle: 'Vos coordonnées',
    clientNameLabel: 'Nom au complet*',
    clientPhoneLabel: 'Téléphone associé*',
    clientEmailLabel: 'Courriel',
    friendSectionTitle: 'Coordonnées de votre ami',
    friendSectionTitleIWasReferred: 'Coordonnées de qui vous a recommandé',
    friendRelationshipLabel: 'Relation avec vous (ami, collègue, etc.)*',
    friendRelationshipDefaultSelect: 'Sélectionner une relation',
    friendNameLabel: 'Nom*',
    friendPhoneLabel: 'Téléphone*',
    friendEmailLabel: 'Courriel',
    termsText:
      'En soumettant ce formulaire, je certifie avoir lu et accepté les conditions du programme de recommandation dans la section FAQ.',
    referButton: 'Recommander',
    referButtonIWasReferred: 'Soumettre',
    successHeaderWasReferred: "Merci de nous l'avoir fait savoir.",
    successBodyWasReferred:
      'Nous confirmerons vos informations et traiterons la recommandation',
    successHeader: 'Merci pour votre recommandation !',
    successBody:
      'Nous vous contacterons une fois que {recipientName} aura emménagé.',
    closeButton: 'Fermer',
    errors: {
      relationshipRequired: 'Relation requis',
      nameRequired: NAME_FORM_ERR_FR,
      namePattern: NAME_FORM_ERR_FR,
      phoneRequired: FORM_ERRORS.FR.PHONE,
      phonePattern: FORM_ERRORS.FR.PHONE,
      emailPattern: EMAIL_FORM_ERR_FR,
    },
  },
};

export const relationshipOptionsByLocal: Record<
  Local,
  { value: string; label: string }[]
> = {
  en: [
    { value: 'friend/familycoworker', label: 'Friend, family or coworker' },
    { value: 'realtor/mover', label: 'Realtor or mover' },
    { value: 'otherBusiness', label: 'Other business' },
  ],
  fr: [
    { value: 'friend/familycoworker', label: 'Ami, famille ou collègue' },
    { value: 'realtor/mover', label: 'Agent immobilier ou déménageur' },
    { value: 'otherBusiness', label: 'Autre entreprise' },
  ],
};

const ClientHubReferral: React.FC<ClientHubReferralProps> = ({
  isMobile,
  activeSection,
  track,
  lcode,
  local,
}) => {
  const id = 'referral';
  const showSection = () => (isMobile ? activeSection === id : true);
  const t = translations[local];
  const relationshipOptions = relationshipOptionsByLocal[local];

  const {
    register,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      clientFullName: '',
      clientEmail: '',
      clientPhone: '',
      friendRelationship: '',
      friendName: '',
      friendEmail: '',
      friendPhone: '',
    },
  });

  const [caseId, setCaseId] = useState('');
  const [clientName, setClientName] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [formDisplayed, setFormDisplayed] = useState('');
  let formSubmissionLock = false;

  const clickRefer = async () => {
    if (formSubmissionLock) return;
    formSubmissionLock = true;
    track('Click Refer');

    const formIsValid = await trigger();
    if (!formIsValid) {
      // eslint-disable-next-line no-console
      console.log('Errors found', errors);
      formSubmissionLock = false;
      return;
    }

    const isAlreadyReferred = formDisplayed === 'received';

    const body = {
      lcode,
      client: {
        fullName: isAlreadyReferred
          ? getValues('friendName')
          : getValues('clientFullName'),
        phone: isAlreadyReferred
          ? getValues('friendPhone')
          : getValues('clientPhone'),
        email: isAlreadyReferred
          ? getValues('friendEmail')
          : getValues('clientEmail'),
      },
      friend: {
        relationship: getValues('friendRelationship'),
        name: isAlreadyReferred
          ? getValues('clientFullName')
          : getValues('friendName'),
        phone: isAlreadyReferred
          ? getValues('clientPhone')
          : getValues('friendPhone'),
        email: isAlreadyReferred
          ? getValues('clientEmail')
          : getValues('friendEmail'),
      },
      isAlreadyReferred,
    };

    const response = await clientHubRefer(body);
    if (response.status === 200) {
      setClientName(getValues('clientFullName'));
      setRecipientName(getValues('friendName'));
      setCaseId('true');
    }
    formSubmissionLock = false;
  };

  const resetForm = () => {
    reset();
    setClientName('');
    setRecipientName('');
    setCaseId('');
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    return new Promise<void>((resolve, reject) => {
      if (document.execCommand('copy')) {
        resolve();
      } else {
        reject();
      }
      textArea.remove();
    });
  };

  const copyCaseId = () =>
    copyToClipboard(
      `${local === 'en' ? 'Referral id' : 'ID de référer'}: ${caseId}`
    );

  return (
    <div style={{ display: showSection() ? 'block' : 'none' }}>
      {/* Success Modal */}
      <div id="success-modal" style={{ display: caseId ? 'flex' : 'none' }}>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'lightgrey',
            opacity: '0.5',
            zIndex: 2000,
          }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            zIndex: 3000,
          }}
        >
          <div
            style={{
              width: '80%',
              minHeight: '10em',
              backgroundColor: 'white',
              borderRadius: '10px',
              padding: '20px',
              opacity: '1',
            }}
          >
            <h3 style={{ textTransform: 'none' }}>
              {formDisplayed === 'send'
                ? t.successHeader
                : t.successHeaderWasReferred}
            </h3>
            <br />
            <div>
              {formDisplayed === 'send'
                ? t.successBody.replace('{recipientName}', recipientName)
                : t.successBodyWasReferred}
            </div>
            <div style={{ display: 'none' }}>
              <br />
              <div>
                <span>
                  <b>
                    {local === 'en' ? 'Referral id' : 'ID de référer'}: {caseId}
                  </b>
                </span>{' '}
                &nbsp;
                <span
                  onClick={() => copyCaseId()}
                  style={{ cursor: 'pointer' }}
                >
                  <ContentCopyOutlinedIcon fontSize={'small'} />
                </span>
              </div>
              <div>
                {local === 'en' ? 'Submitted by' : 'Soumis par'}: {clientName}
              </div>
              <div>
                {local === 'en' ? 'Recipient' : 'Destinataire'}: {recipientName}
              </div>
              <br />
            </div>
            <br />
            <div>{local === 'en' ? 'Talk soon!' : 'À bientôt !'}</div>
            <br />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  border: '2px solid lightgrey',
                  borderRadius: '5px',
                  padding: '8px 16px',
                }}
                onClick={() => {
                  resetForm();
                }}
              >
                {t.closeButton}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Form */}
      <h3
        id="referral"
        style={{ textTransform: 'none', backgroundColor: 'aliceblue' }}
      >
        {t.referralTitle}
      </h3>
      <div>{t.description}</div>
      <br />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '20px 0',
        }}
      >
        <div
          style={{
            padding: '10px 0',
            margin: '0 10px',
            textAlign: 'center',
            width: '50%',
            backgroundColor:
              formDisplayed === 'send'
                ? 'rgba(240, 248, 255)'
                : 'rgba(240, 248, 255, 0.5)',
            boxShadow:
              formDisplayed === 'send'
                ? 'lightblue 0px 0px 24px -16px inset'
                : '',
            borderBottom: formDisplayed === 'send' ? '2px solid lightblue' : '',
          }}
          onClick={() =>
            setFormDisplayed(formDisplayed === 'send' ? '' : 'send')
          }
        >
          Recommander
        </div>
        <div
          style={{
            padding: '10px 0',
            margin: '0 10px',
            textAlign: 'center',
            width: '50%',
            backgroundColor:
              formDisplayed === 'received'
                ? 'rgba(240, 248, 255)'
                : 'rgba(240, 248, 255, 0.5)',
            boxShadow:
              formDisplayed === 'received'
                ? 'lightblue 0px 0px 24px -16px inset'
                : '',
            borderBottom:
              formDisplayed === 'received' ? '2px solid lightblue' : '',
          }}
          onClick={() =>
            setFormDisplayed(formDisplayed === 'received' ? '' : 'received')
          }
        >
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          On m'a recommandé
        </div>
      </div>
      <div
        style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}
      />
      <form style={{ display: formDisplayed === '' ? 'none' : '' }}>
        {/* Client Info */}
        <div>
          <b>{t.clientSectionTitle}</b>
        </div>
        <div>
          <div>
            <div>
              <label key="name" className={style.formLabel}>
                {t.clientNameLabel}
              </label>
              <div>
                <input
                  type="text"
                  style={{
                    width: '100%',
                    marginBottom: '10px',
                    boxSizing: 'border-box',
                    background: 'rgba(224, 225, 227, 0.14)',
                    border: '1px solid rgba(2, 35, 94, 0.2)',
                    borderRadius: '4px',
                  }}
                  {...register('clientFullName', {
                    required: t.errors.nameRequired,
                    pattern: {
                      value: /^[a-zA-Z\s]{0,50}$/,
                      message: t.errors.namePattern,
                    },
                  })}
                />
                {errors.clientFullName?.message && (
                  <div className="error-msg text-end">
                    {errors.clientFullName.message}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label>{t.clientPhoneLabel}</label>
              <div>
                <input
                  type="tel"
                  id="phone"
                  style={{
                    width: '100%',
                    marginBottom: '10px',
                    boxSizing: 'border-box',
                    background: 'rgba(224, 225, 227, 0.14)',
                    border: '1px solid rgba(2, 35, 94, 0.2)',
                    borderRadius: '4px',
                  }}
                  {...register('clientPhone', {
                    required: t.errors.phoneRequired,
                    pattern: {
                      value: /^(?![01])[0-9]{10}$/,
                      message: t.errors.phonePattern,
                    },
                  })}
                />
                {errors.clientPhone?.message && (
                  <div className="error-msg text-end">
                    {errors.clientPhone.message}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <label>{t.clientEmailLabel}</label>
            <div>
              <input
                type="email"
                id="email"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                  background: 'rgba(224, 225, 227, 0.14)',
                  border: '1px solid rgba(2, 35, 94, 0.2)',
                  borderRadius: '4px',
                }}
                {...register('clientEmail', {
                  required: false,
                  pattern: {
                    value: /^$|^\S+@\S+$/i,
                    message: t.errors.emailPattern,
                  },
                })}
              />
            </div>
          </div>
        </div>

        <br />

        {/* Friend Info */}
        <div>
          <b>
            {formDisplayed === 'send'
              ? t.friendSectionTitle
              : t.friendSectionTitleIWasReferred}
          </b>
        </div>
        <div>
          <div className={style.formRow}>
            <label key="name" className={style.formLabel}>
              {t.friendRelationshipLabel}
            </label>
            <div>
              <select
                className="form-select"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                  background: 'rgba(224, 225, 227, 0.14)',
                  border: '1px solid rgba(2, 35, 94, 0.2)',
                  borderRadius: '4px',
                  padding: '0.063rem 1.25rem 0.063rem 0.75rem',
                }}
                {...register('friendRelationship', {
                  required: t.errors.relationshipRequired,
                })}
                defaultValue=""
              >
                <option value="" disabled>
                  {t.friendRelationshipDefaultSelect}
                </option>
                {relationshipOptions.length
                  ? relationshipOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))
                  : ''}
              </select>
              {errors.friendRelationship?.message && (
                <div className="error-msg text-end">
                  {errors.friendRelationship.message}
                </div>
              )}
            </div>
          </div>

          <div className={style.formRow}>
            <label key="name" className={style.formLabel}>
              {t.friendNameLabel}
            </label>
            <div>
              <input
                type="text"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                  background: 'rgba(224, 225, 227, 0.14)',
                  border: '1px solid rgba(2, 35, 94, 0.2)',
                  borderRadius: '4px',
                }}
                {...register('friendName', {
                  required: t.errors.nameRequired,
                  pattern: {
                    value: /^[a-zA-Z\s]{0,50}$/,
                    message: t.errors.namePattern,
                  },
                })}
              />
              {errors.friendName?.message && (
                <div className="error-msg text-end">
                  {errors.friendName.message}
                </div>
              )}
            </div>
          </div>

          <div>
            <label>{t.friendPhoneLabel}</label>
            <div>
              <input
                type="tel"
                id="phone"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                  background: 'rgba(224, 225, 227, 0.14)',
                  border: '1px solid rgba(2, 35, 94, 0.2)',
                  borderRadius: '4px',
                }}
                {...register('friendPhone', {
                  required: t.errors.phoneRequired,
                  pattern: {
                    value: /^(?![01])[0-9]{10}$/,
                    message: t.errors.phonePattern,
                  },
                })}
              />
              {errors.friendPhone?.message && (
                <div className="error-msg text-end">
                  {errors.friendPhone.message}
                </div>
              )}
            </div>
          </div>

          <div>
            <label>{t.friendEmailLabel}</label>
            <div>
              <input
                type="email"
                id="email"
                style={{
                  width: '100%',
                  marginBottom: '10px',
                  boxSizing: 'border-box',
                  background: 'rgba(224, 225, 227, 0.14)',
                  border: '1px solid rgba(2, 35, 94, 0.2)',
                  borderRadius: '4px',
                }}
                {...register('friendEmail', {
                  required: false,
                  pattern: {
                    value: /^$|^\S+@\S+$/i,
                    message: t.errors.emailPattern,
                  },
                })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', margin: '10px 0' }}>
            <div style={{ fontSize: 'small' }}>{t.termsText}</div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '10px 0',
            }}
          >
            <Button
              variant="contained"
              style={{
                backgroundColor: '#0aa961',
                marginBottom: 12,
                color: 'white',
              }}
              onClick={() => clickRefer()}
            >
              {formDisplayed === 'send'
                ? t.referButton
                : t.referButtonIWasReferred}
            </Button>
          </div>
        </div>
      </form>

      <hr />
      <br />
    </div>
  );
};

export default ClientHubReferral;
